<script setup lang="ts">
import { computed, readonly } from 'vue';
import { useStore } from 'vuex';
import { useForm } from 'vee-validate';
import { yup } from '@clearbanc/validation';

import { JobRoles, UserResponseDto } from '@clearbanc/baus-sdk';
import { OWNER_ROLES, ownerJobTitles } from '@clearbanc/data-common-types';
import { JobTitlesByCorpCountry, UserJobTitleProps } from '@/data/owner';
import analytics, { SEGMENT_EVENTS } from '@/utils/analytics';
import { useToast } from '@/composables/useToast';

import CheckboxGroup from '@/components/forms/groups/CheckboxGroup.vue';
import InputTextGroup from '@/components/forms/groups/InputTextGroup.vue';
import DropdownGroup from '@/components/forms/groups/DropdownGroup.vue';
import DSButton from 'primevue/button';
import Message from 'primevue/message';

// -----
// Types
// -----

interface FormValues {
  firstName: string;
  lastName: string;
  email: string;
  jobTitle: UserJobTitleProps | null;
  ownerRole: string[];
}

// -----
// Setup
// -----

const store = useStore();
const toast = useToast();
const props = defineProps<{
  isBusinessOwnersPage: boolean;
}>();
const emits = defineEmits<{
  success: [user: UserResponseDto];
  cancel: [];
}>();

// ---------
// Constants
// ---------

const requestStatuses = readonly({
  createNonPrimaryUser: computed(() =>
    store.getters.requestStatus('CREATE_NON_PRIMARY_USER'),
  ),
  createUserInvite: computed(() =>
    store.getters.requestStatus('CREATE_USER_INVITE'),
  ),
  inviteUser: computed(() => store.getters.requestStatus('INVITE_USER')),
});

const jobTitles = computed(() =>
  JobTitlesByCorpCountry(store.getters.business.corpCountry),
);

const handleError = (error: any): void => {
  toast.error(error);
};

const validationSchema = yup.object({
  firstName: yup.userName().required(),
  lastName: yup.userName().required(),
  email: yup.email().required(),
  jobTitle: yup.jobTitle().required(),
});

// ---------
// Variables
// ---------

const { values, handleSubmit, isSubmitting, errors } = useForm<FormValues>({
  validationSchema,
  initialValues: {
    firstName: '',
    lastName: '',
    email: '',
    jobTitle: null,
    ownerRole: [],
  },
});

const isFormStateValid = computed(() => {
  try {
    validationSchema.validateSync(values, { abortEarly: false });
    return true;
  } catch {
    return false;
  }
});

const hasErrors = computed(
  () => !isFormStateValid.value || !!Object.keys(errors.value).length,
);

const isOwnerJobTitle = computed<boolean>(() => {
  if (!values?.jobTitle) return true;

  return ownerJobTitles.includes(values.jobTitle.value);
});

const hasOwnerRole = computed<boolean>(() => {
  if (!values?.ownerRole) return false;

  return values.ownerRole.includes(JobRoles.Owner);
});

// ----
// Form
// ----

const onSubmit = handleSubmit(async (formValues: FormValues) => {
  analytics.track(SEGMENT_EVENTS.if_send_invite_clicked);
  const jobRoles: string[] = [];
  if (formValues.ownerRole?.includes(OWNER_ROLES.OWNER)) {
    jobRoles.push(OWNER_ROLES.OWNER);
  }

  const payload = {
    firstName: formValues.firstName,
    lastName: formValues.lastName,
    email: formValues.email,
    jobTitle: formValues.jobTitle?.value,
    jobRoles: jobRoles,
  };

  let newUser: UserResponseDto;

  newUser = await store.dispatchApiAction('INVITE_USER', {
    inviteNewUserDto: payload,
    businessId: store.getters.business.id,
  });
  if (requestStatuses.inviteUser.isError) {
    handleError(requestStatuses.inviteUser.error);
    return;
  }

  analytics.track(SEGMENT_EVENTS.if_send_invite_success, {
    invited_user_email: formValues.email,
    invited_user_userid: newUser.id,
    invited_user_job_roles: newUser.jobRoles,
    invited_user_job_title: newUser.jobTitle,
    business_id: store.getters.business.id,
  });

  emits('success', newUser);
});
</script>

<template>
  <form
    class="c-form"
    :style="{ pointerEvents: isSubmitting ? 'none' : 'all' }"
    @submit.prevent="onSubmit"
  >
    <div class="c-group px-1">
      <!-- Row 1: First Name & Last Name -->
      <div class="c-form__row">
        <div class="form-fields">
          <div class="field-wrapper">
            <InputTextGroup name="firstName" label="First Name" />
          </div>
          <div class="field-wrapper">
            <InputTextGroup name="lastName" label="Last Name" />
          </div>
        </div>
      </div>
      <!-- Row 2: Email & Job Title -->
      <div class="c-form__row">
        <div class="form-fields">
          <div class="field-wrapper">
            <InputTextGroup name="email" label="Email Address" />
          </div>
          <div class="field-wrapper">
            <DropdownGroup
              name="jobTitle"
              label="Job Title"
              option-label="label"
              :options="jobTitles"
              placeholder="Select job title"
            />
          </div>
        </div>
      </div>
      <!-- Row 3: Ownership Checkbox -->
      <div class="c-form__row">
        <div class="checkbox-container">
          <CheckboxGroup
            name="ownerRole"
            :options="[
              {
                value: OWNER_ROLES.OWNER,
                label: 'This individual has 25%+ ownership',
              },
            ]"
          />
        </div>
      </div>

      <!-- Row 4: Ownership Message -->
      <Message
        v-if="props.isBusinessOwnersPage && !isOwnerJobTitle && !hasOwnerRole"
        severity="info"
        :closable="false"
        ><p>
          This team member will not be added as a Business Owner due to
          eligibility criteria.
        </p>
      </Message>

      <!-- Row 5: Buttons -->
      <div class="c-form__row actions">
        <div class="form-fields">
          <div class="field-wrapper">
            <DSButton
              label="Cancel"
              class="p-button-outlined w-full"
              @click="emits('cancel')"
            />
          </div>
          <div class="field-wrapper">
            <DSButton
              label="Send invite"
              :loading="isSubmitting"
              :disabled="hasErrors || isSubmitting"
              class="w-full"
              type="submit"
            />
          </div>
        </div>
      </div>
    </div>
  </form>
</template>

<style scoped>
.c-form__row + .c-form__row {
  margin-top: 1rem;
}

.checkbox-container {
  padding: 0.5rem;
}

.form-fields {
  display: flex;
  gap: 1rem;
}

.field-wrapper {
  flex: 1;
}

.actions {
  margin-top: 2.5rem;
}

@media screen and (max-width: 500px) {
  .form-fields {
    flex-direction: column;
    gap: 1rem;
  }

  .field-wrapper {
    width: 100%;
  }
}
</style>
